import { PhysioLayout } from "@/components/layout/physio-layout";
import { Star, CheckCircle } from "lucide-react";
import { Link } from "wouter";

const APP_PILLARS_SUMMARY = [
  { pillar: "Clinical Practice", examples: "Advanced assessment, complex case management, extended scope, injection therapy" },
  { pillar: "Leadership & Management", examples: "Service development, clinical governance, team leadership, strategic planning" },
  { pillar: "Education", examples: "Teaching staff and students, curriculum development, clinical supervision" },
  { pillar: "Research", examples: "Evidence appraisal, audit, QI projects, dissemination of findings" },
];

const CONSULTANT_PATHWAY = [
  "Typically Band 8c–8d with several years at Band 8a/b Advanced Practitioner level",
  "Formal recognition via HEE consultant practitioner framework (portfolio-based assessment)",
  "Strategic leadership: developing and implementing rheumatology physiotherapy strategy at service or system level",
  "Significant research or service development output",
  "Teaching at postgraduate level (MSc, clinical academic collaborations)",
  "National involvement: ACPR committee, CSP policy development, NICE consultation",
];

export default function AppConsultantPathway() {
  return (
    <PhysioLayout title="Advanced & Consultant Practitioner Pathways">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-violet-800 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Star className="h-8 w-8 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">Advanced & Consultant Physiotherapist</h2>
              <p className="text-violet-300 text-sm">Band 8a–8d · HEE APP Framework · Consultant Physiotherapist</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm leading-relaxed">
            Advanced Physiotherapy Practitioners (APPs) operate at the highest level of clinical practice, integrating clinical expertise with leadership, education, and research. Consultant Physiotherapists are the pinnacle of clinical career progression.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">APP Band Structure</h3>
          <div className="space-y-3">
            {[
              { band: "Band 8a", title: "Advanced Physiotherapy Practitioner", desc: "Expert clinical practice in a defined specialist area; leads specific aspects of service; mentors and supervises clinical staff" },
              { band: "Band 8b", title: "Senior Advanced Physiotherapy Practitioner", desc: "Leads clinical and operational aspects of a service; significant research or education contribution; clinical lead for rheumatology physiotherapy" },
              { band: "Band 8c/d", title: "Consultant Physiotherapist", desc: "Strategic leadership at service and system level; formal consultant recognition; significant national-level contribution to the profession" },
            ].map((row) => (
              <div key={row.band} className="flex items-start gap-4 p-4 bg-violet-50 border border-violet-100 rounded-xl">
                <div className="w-16 text-center">
                  <span className="text-xs font-bold text-violet-700 bg-violet-100 px-2 py-1 rounded-full">{row.band}</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{row.title}</p>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{row.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">Four APP Pillars — Requirements</h3>
            <Link href="/physio/cpd/app-portfolio">
              <button className="text-xs text-violet-700 hover:text-violet-800 font-medium">Open APP Portfolio →</button>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {APP_PILLARS_SUMMARY.map((p) => (
              <div key={p.pillar} className="bg-violet-50 border border-violet-100 rounded-xl p-4">
                <p className="font-semibold text-violet-800 text-sm">{p.pillar}</p>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">{p.examples}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">
            All four pillars must be evidenced for formal APP recognition. Evidence is mapped in your APP Portfolio tracker and reviewed by an appropriately experienced assessor.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Consultant Physiotherapist Pathway</h3>
          <ul className="space-y-2">
            {CONSULTANT_PATHWAY.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle className="h-4 w-4 text-violet-500 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-5">
          <h3 className="font-bold text-slate-900 mb-2">Getting Formal APP Recognition</h3>
          <p className="text-sm text-slate-600 mb-3 leading-relaxed">
            Formal Advanced Practitioner recognition in the NHS is typically achieved through one of these routes:
          </p>
          <div className="space-y-2 text-sm text-slate-700">
            {[
              "HEE / NHSE Faculty of Advancing Practice — multi-professional advanced practice portfolio assessment",
              "Higher Education Institution — MSc or PG Diploma in Advanced Practice (HCPC registered)",
              "CSP advanced practice credential scheme (in development)",
              "Trust-level advanced practitioner framework — local credentialing with portfolio evidence",
            ].map((route) => (
              <div key={route} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-violet-500 shrink-0 mt-2" />
                {route}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PhysioLayout>
  );
}
